import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  BookOpen, Search, Plus, Filter, RefreshCw, CheckCircle2, Clock, AlertTriangle,
  FileText, Tag, ChevronRight, Eye, Edit, Trash2, Send, Check, MessageSquare,
  Lightbulb, Target, Users, Archive, Download, Sparkles, Brain, Wand2, FileSearch, Loader2
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

export default function LessonsLearnedPage() {
  const { t } = useTranslation();
  const token = localStorage.getItem('token');
  const [stats, setStats] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // AI Features
  const [showAIRecommendDialog, setShowAIRecommendDialog] = useState(false);
  const [showAIGenerateDialog, setShowAIGenerateDialog] = useState(false);
  const [aiRecommendContent, setAIRecommendContent] = useState('');
  const [aiRecommendContentType, setAIRecommendContentType] = useState('incident');
  const [aiRecommendations, setAIRecommendations] = useState(null);
  const [aiGenerating, setAIGenerating] = useState(false);
  const [investigations, setInvestigations] = useState([]);
  const [selectedInvestigation, setSelectedInvestigation] = useState('');
  const [generatedLesson, setGeneratedLesson] = useState(null);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Enums
  const [categories, setCategories] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  
  // New lesson form
  const [newLesson, setNewLesson] = useState({
    title: '',
    summary: '',
    source: 'manual',
    category: 'flight_operations',
    severity: 'medium',
    what_happened: '',
    why_it_happened: '',
    what_we_learned: '',
    recommendations: [''],
    tags: []
  });
  const [tagInput, setTagInput] = useState('');

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Fetch data
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned/stats`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchLessons = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/lessons-learned?limit=50`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      if (categoryFilter) url += `&category=${categoryFilter}`;
      if (severityFilter) url += `&severity=${severityFilter}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      
      const res = await fetch(url, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setLessons(data.lessons || []);
      }
    } catch (err) {
      console.error('Error fetching lessons:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEnums = async () => {
    try {
      const [catRes, sevRes, srcRes, statRes] = await Promise.all([
        fetch(`${API_URL}/api/lessons-learned/enums/categories`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/lessons-learned/enums/severities`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/lessons-learned/enums/sources`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/lessons-learned/enums/statuses`, { headers: apiHeaders })
      ]);
      
      if (catRes.ok) setCategories(await catRes.json());
      if (sevRes.ok) setSeverities(await sevRes.json());
      if (srcRes.ok) setSources(await srcRes.json());
      if (statRes.ok) setStatuses(await statRes.json());
    } catch (err) {
      console.error('Error fetching enums:', err);
    }
  };

  const fetchLessonDetail = async (lessonId) => {
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned/${lessonId}`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setSelectedLesson(data);
        setShowDetailDialog(true);
      }
    } catch (err) {
      toast.error('Error loading lesson');
    }
  };

  // Actions
  const createLesson = async () => {
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({
          ...newLesson,
          recommendations: newLesson.recommendations.filter(r => r.trim())
        })
      });
      
      if (res.ok) {
        toast.success('Lesson created successfully');
        setShowCreateDialog(false);
        setNewLesson({
          title: '',
          summary: '',
          source: 'manual',
          category: 'flight_operations',
          severity: 'medium',
          what_happened: '',
          why_it_happened: '',
          what_we_learned: '',
          recommendations: [''],
          tags: []
        });
        fetchLessons();
        fetchStats();
      } else {
        toast.error('Failed to create lesson');
      }
    } catch (err) {
      toast.error('Error creating lesson');
    }
  };

  const submitForReview = async (lessonId) => {
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned/${lessonId}/submit-review`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Lesson submitted for review');
        fetchLessons();
        fetchStats();
      }
    } catch (err) {
      toast.error('Error submitting lesson');
    }
  };

  const approveLesson = async (lessonId) => {
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned/${lessonId}/approve`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Lesson approved');
        fetchLessons();
        fetchStats();
      }
    } catch (err) {
      toast.error('Error approving lesson');
    }
  };

  const distributeLesson = async (lessonId) => {
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned/${lessonId}/distribute?method=bulletin&require_ack=true`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Lesson distributed');
        fetchLessons();
        fetchStats();
      }
    } catch (err) {
      toast.error('Error distributing lesson');
    }
  };

  const acknowledgeLesson = async (lessonId) => {
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned/${lessonId}/acknowledge`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        toast.success('Lesson acknowledged');
        if (selectedLesson) {
          fetchLessonDetail(lessonId);
        }
      }
    } catch (err) {
      toast.error('Error acknowledging lesson');
    }
  };

  const seedLessons = async () => {
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned/seed`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Seeded ${data.lessons_created} sample lessons`);
        fetchLessons();
        fetchStats();
      }
    } catch (err) {
      toast.error('Error seeding lessons');
    }
  };

  // AI Features
  const fetchInvestigations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/investigations?status=completed&limit=50`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setInvestigations(data.investigations || data || []);
      }
    } catch (err) {
      console.error('Error fetching investigations:', err);
    }
  };

  const getAIRecommendations = async () => {
    if (!aiRecommendContent.trim()) {
      toast.error('Please enter content to analyze');
      return;
    }
    setAIGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned/ai/recommend`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({
          content: aiRecommendContent,
          content_type: aiRecommendContentType,
          limit: 5
        })
      });
      if (res.ok) {
        const data = await res.json();
        setAIRecommendations(data);
        toast.success('AI recommendations generated');
      } else {
        toast.error('Failed to get recommendations');
      }
    } catch (err) {
      toast.error('Error getting AI recommendations');
    } finally {
      setAIGenerating(false);
    }
  };

  const generateLessonFromInvestigation = async () => {
    if (!selectedInvestigation) {
      toast.error('Please select an investigation');
      return;
    }
    setAIGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned/ai/generate-from-investigation/${selectedInvestigation}`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        setGeneratedLesson(data);
        toast.success('Lesson generated! Review and save.');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate lesson');
      }
    } catch (err) {
      toast.error('Error generating lesson');
    } finally {
      setAIGenerating(false);
    }
  };

  const saveGeneratedLesson = async () => {
    if (!generatedLesson?.lesson_data) return;
    setAIGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/lessons-learned/ai/save-generated`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({
          lesson_data: generatedLesson.lesson_data,
          source_investigation_id: selectedInvestigation || null
        })
      });
      if (res.ok) {
        toast.success('AI-generated lesson saved as draft');
        setShowAIGenerateDialog(false);
        setGeneratedLesson(null);
        setSelectedInvestigation('');
        fetchLessons();
        fetchStats();
      } else {
        toast.error('Failed to save lesson');
      }
    } catch (err) {
      toast.error('Error saving lesson');
    } finally {
      setAIGenerating(false);
    }
  };

  const updateGeneratedLesson = (field, value) => {
    if (!generatedLesson?.lesson_data) return;
    setGeneratedLesson({
      ...generatedLesson,
      lesson_data: {
        ...generatedLesson.lesson_data,
        [field]: value
      }
    });
  };

  useEffect(() => {
    fetchStats();
    fetchLessons();
    fetchEnums();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLessons();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, categoryFilter, severityFilter, statusFilter]);

  // Helpers
  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/30',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      low: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      informational: 'bg-slate-500/20 text-slate-400 border-slate-500/30'
    };
    return colors[severity] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-slate-500/20 text-slate-400',
      pending_review: 'bg-yellow-500/20 text-yellow-400',
      approved: 'bg-green-500/20 text-green-400',
      distributed: 'bg-blue-500/20 text-blue-400',
      archived: 'bg-slate-600/20 text-slate-500'
    };
    return colors[status] || colors.draft;
  };

  const addRecommendation = () => {
    setNewLesson({...newLesson, recommendations: [...newLesson.recommendations, '']});
  };

  const updateRecommendation = (idx, value) => {
    const updated = [...newLesson.recommendations];
    updated[idx] = value;
    setNewLesson({...newLesson, recommendations: updated});
  };

  const removeRecommendation = (idx) => {
    const updated = newLesson.recommendations.filter((_, i) => i !== idx);
    setNewLesson({...newLesson, recommendations: updated.length ? updated : ['']});
  };

  const addTag = () => {
    if (tagInput.trim() && !newLesson.tags.includes(tagInput.trim().toLowerCase())) {
      setNewLesson({...newLesson, tags: [...newLesson.tags, tagInput.trim().toLowerCase()]});
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setNewLesson({...newLesson, tags: newLesson.tags.filter(t => t !== tag)});
  };

  return (
    <div className="space-y-6" data-testid="lessons-learned-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100 flex items-center gap-2">
            <BookOpen className="w-7 h-7 text-amber-400" />
            {t('lessonsLearned.title')}
          </h1>
          <p className="text-slate-400 mt-1">{t('lessonsLearned.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setShowAIRecommendDialog(true); }}>
            <FileSearch className="w-4 h-4 mr-2" />
            AI Recommend
          </Button>
          <Button variant="outline" onClick={() => { setShowAIGenerateDialog(true); fetchInvestigations(); }} className="border-purple-500/50 text-purple-400 hover:bg-purple-500/20">
            <Wand2 className="w-4 h-4 mr-2" />
            AI Generate
          </Button>
          <Button variant="outline" onClick={seedLessons}>
            <Sparkles className="w-4 h-4 mr-2" />
            Seed Samples
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-lesson-btn">
            <Plus className="w-4 h-4 mr-2" />
            {t('lessonsLearned.newLesson')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Total Lessons</p>
                <p className="text-2xl font-bold text-slate-100">{stats?.total_lessons || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/20">
                <BookOpen className="w-5 h-5 text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Approved</p>
                <p className="text-2xl font-bold text-green-400">{stats?.by_status?.approved || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Pending Review</p>
                <p className="text-2xl font-bold text-yellow-400">{stats?.pending_review || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-yellow-500/20">
                <Clock className="w-5 h-5 text-yellow-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Distributed</p>
                <p className="text-2xl font-bold text-blue-400">{stats?.by_status?.distributed || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20">
                <Send className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider">Draft</p>
                <p className="text-2xl font-bold text-slate-400">{stats?.by_status?.draft || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-slate-500/20">
                <FileText className="w-5 h-5 text-slate-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input 
                  placeholder="Search lessons..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-900/50"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select value={categoryFilter || 'all'} onValueChange={(v) => setCategoryFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px] bg-slate-900/50">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={severityFilter || 'all'} onValueChange={(v) => setSeverityFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px] bg-slate-900/50">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severities</SelectItem>
                {severities.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[150px] bg-slate-900/50">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map(s => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => { setSearchQuery(''); setCategoryFilter(''); setSeverityFilter(''); setStatusFilter(''); }}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lessons List */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-slate-100">Lessons ({lessons.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="w-8 h-8 text-slate-400 mx-auto animate-spin" />
              <p className="text-slate-400 mt-2">Loading lessons...</p>
            </div>
          ) : lessons.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No lessons found</p>
              <p className="text-sm text-slate-500 mt-1">Create a new lesson or seed sample data</p>
            </div>
          ) : (
            <div className="space-y-3">
              {lessons.map(lesson => (
                <div 
                  key={lesson.id} 
                  className="p-4 rounded-lg bg-slate-900/50 border border-slate-700 hover:border-slate-600 transition-colors cursor-pointer"
                  onClick={() => fetchLessonDetail(lesson.id)}
                  data-testid={`lesson-${lesson.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-slate-500 font-mono">{lesson.reference_number}</span>
                        <Badge className={getSeverityColor(lesson.severity)}>{lesson.severity}</Badge>
                        <Badge className={getStatusColor(lesson.status)}>{lesson.status?.replace('_', ' ')}</Badge>
                      </div>
                      <h3 className="font-medium text-slate-200">{lesson.title}</h3>
                      <p className="text-sm text-slate-400 mt-1 line-clamp-2">{lesson.summary}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <span className="text-xs text-slate-500 capitalize flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {lesson.category?.replace('_', ' ')}
                        </span>
                        <span className="text-xs text-slate-500 capitalize flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {lesson.source}
                        </span>
                        {lesson.tags?.length > 0 && (
                          <div className="flex items-center gap-1">
                            {lesson.tags.slice(0, 3).map(tag => (
                              <span key={tag} className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400">{tag}</span>
                            ))}
                            {lesson.tags.length > 3 && (
                              <span className="text-xs text-slate-500">+{lesson.tags.length - 3}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-500" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Lesson Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-amber-400" />
              Create New Lesson Learned
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Title *</Label>
                <Input 
                  value={newLesson.title}
                  onChange={(e) => setNewLesson({...newLesson, title: e.target.value})}
                  placeholder="Brief descriptive title"
                  className="bg-slate-800/50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Source</Label>
                <Select value={newLesson.source} onValueChange={(v) => setNewLesson({...newLesson, source: v})}>
                  <SelectTrigger className="bg-slate-800/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sources.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Summary *</Label>
              <Textarea 
                value={newLesson.summary}
                onChange={(e) => setNewLesson({...newLesson, summary: e.target.value})}
                placeholder="Brief summary of the lesson"
                className="bg-slate-800/50"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Category</Label>
                <Select value={newLesson.category} onValueChange={(v) => setNewLesson({...newLesson, category: v})}>
                  <SelectTrigger className="bg-slate-800/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Severity</Label>
                <Select value={newLesson.severity} onValueChange={(v) => setNewLesson({...newLesson, severity: v})}>
                  <SelectTrigger className="bg-slate-800/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {severities.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator className="bg-slate-700" />

            <div className="space-y-2">
              <Label className="text-slate-300">What Happened?</Label>
              <Textarea 
                value={newLesson.what_happened}
                onChange={(e) => setNewLesson({...newLesson, what_happened: e.target.value})}
                placeholder="Describe the event or situation"
                className="bg-slate-800/50"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Why It Happened?</Label>
              <Textarea 
                value={newLesson.why_it_happened}
                onChange={(e) => setNewLesson({...newLesson, why_it_happened: e.target.value})}
                placeholder="Root cause analysis"
                className="bg-slate-800/50"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">What We Learned</Label>
              <Textarea 
                value={newLesson.what_we_learned}
                onChange={(e) => setNewLesson({...newLesson, what_we_learned: e.target.value})}
                placeholder="Key takeaways and insights"
                className="bg-slate-800/50"
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Recommendations</Label>
                <Button variant="ghost" size="sm" onClick={addRecommendation}>
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </div>
              {newLesson.recommendations.map((rec, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input 
                    value={rec}
                    onChange={(e) => updateRecommendation(idx, e.target.value)}
                    placeholder={`Recommendation ${idx + 1}`}
                    className="bg-slate-800/50"
                  />
                  {newLesson.recommendations.length > 1 && (
                    <Button variant="ghost" size="sm" onClick={() => removeRecommendation(idx)}>
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label className="text-slate-300">Tags</Label>
              <div className="flex items-center gap-2">
                <Input 
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add tag and press Enter"
                  className="bg-slate-800/50"
                />
                <Button variant="outline" onClick={addTag}>Add</Button>
              </div>
              {newLesson.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {newLesson.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="cursor-pointer" onClick={() => removeTag(tag)}>
                      {tag} ×
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={createLesson} disabled={!newLesson.title || !newLesson.summary}>
              Create Lesson
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          {selectedLesson && (
            <>
              <DialogHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs text-slate-500 font-mono">{selectedLesson.reference_number}</span>
                    <DialogTitle className="text-slate-100 mt-1">{selectedLesson.title}</DialogTitle>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getSeverityColor(selectedLesson.severity)}>{selectedLesson.severity}</Badge>
                    <Badge className={getStatusColor(selectedLesson.status)}>{selectedLesson.status?.replace('_', ' ')}</Badge>
                  </div>
                </div>
              </DialogHeader>
              
              <div className="space-y-4">
                <p className="text-slate-300">{selectedLesson.summary}</p>
                
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="text-slate-400">Category: <span className="text-slate-200 capitalize">{selectedLesson.category?.replace('_', ' ')}</span></span>
                  <span className="text-slate-400">Source: <span className="text-slate-200 capitalize">{selectedLesson.source}</span></span>
                  <span className="text-slate-400">Created: <span className="text-slate-200">{new Date(selectedLesson.created_at).toLocaleDateString()}</span></span>
                </div>

                {selectedLesson.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedLesson.tags.map(tag => (
                      <Badge key={tag} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                )}

                <Separator className="bg-slate-700" />

                {selectedLesson.what_happened && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-400" />
                      What Happened
                    </h4>
                    <p className="text-slate-400 text-sm whitespace-pre-wrap">{selectedLesson.what_happened}</p>
                  </div>
                )}

                {selectedLesson.why_it_happened && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4 text-blue-400" />
                      Why It Happened
                    </h4>
                    <p className="text-slate-400 text-sm whitespace-pre-wrap">{selectedLesson.why_it_happened}</p>
                  </div>
                )}

                {selectedLesson.what_we_learned && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-amber-400" />
                      What We Learned
                    </h4>
                    <p className="text-slate-400 text-sm whitespace-pre-wrap">{selectedLesson.what_we_learned}</p>
                  </div>
                )}

                {selectedLesson.recommendations?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Recommendations
                    </h4>
                    <ul className="space-y-2">
                      {selectedLesson.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-400">
                          <span className="text-green-400 mt-0.5">•</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <Separator className="bg-slate-700" />

              <div className="flex items-center justify-between">
                <div className="text-sm text-slate-500">
                  {selectedLesson.acknowledgment_count || 0} acknowledgments
                </div>
                <div className="flex items-center gap-2">
                  {selectedLesson.status === 'draft' && (
                    <Button variant="outline" onClick={() => submitForReview(selectedLesson.id)}>
                      <Send className="w-4 h-4 mr-2" />
                      Submit for Review
                    </Button>
                  )}
                  {selectedLesson.status === 'pending_review' && (
                    <Button onClick={() => approveLesson(selectedLesson.id)}>
                      <Check className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                  )}
                  {selectedLesson.status === 'approved' && (
                    <Button onClick={() => distributeLesson(selectedLesson.id)}>
                      <Send className="w-4 h-4 mr-2" />
                      Distribute
                    </Button>
                  )}
                  {selectedLesson.status === 'distributed' && (
                    <Button variant="outline" onClick={() => acknowledgeLesson(selectedLesson.id)}>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Acknowledge
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* AI Recommend Lessons Dialog */}
      <Dialog open={showAIRecommendDialog} onOpenChange={setShowAIRecommendDialog}>
        <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              AI-Powered Lesson Recommendations
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Content Type</Label>
              <Select value={aiRecommendContentType} onValueChange={setAIRecommendContentType}>
                <SelectTrigger className="bg-slate-800/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="incident">Incident Report</SelectItem>
                  <SelectItem value="investigation">Investigation</SelectItem>
                  <SelectItem value="hazard">Hazard Report</SelectItem>
                  <SelectItem value="audit">Audit Finding</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Describe the Incident/Event</Label>
              <Textarea 
                value={aiRecommendContent}
                onChange={(e) => setAIRecommendContent(e.target.value)}
                placeholder="Paste the incident description, investigation summary, or hazard report here. The AI will analyze and recommend relevant existing lessons..."
                className="bg-slate-800/50 min-h-[120px]"
              />
            </div>
            <Button onClick={getAIRecommendations} disabled={aiGenerating || !aiRecommendContent.trim()} className="w-full">
              {aiGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Brain className="w-4 h-4 mr-2" />}
              {aiGenerating ? 'Analyzing...' : 'Get AI Recommendations'}
            </Button>

            {aiRecommendations && (
              <div className="space-y-4 mt-4">
                {aiRecommendations.analysis_summary && (
                  <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                    <p className="text-sm text-purple-300">{aiRecommendations.analysis_summary}</p>
                  </div>
                )}
                
                {aiRecommendations.key_themes?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs text-slate-400">Key Themes:</span>
                    {aiRecommendations.key_themes.map(theme => (
                      <Badge key={theme} variant="outline" className="text-xs">{theme}</Badge>
                    ))}
                  </div>
                )}

                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-300">Recommended Lessons ({aiRecommendations.recommendations?.length || 0})</h4>
                  {aiRecommendations.recommendations?.length === 0 ? (
                    <p className="text-sm text-slate-500">No relevant lessons found. Consider creating a new lesson for this event.</p>
                  ) : (
                    aiRecommendations.recommendations?.map((rec, idx) => (
                      <div key={idx} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700 cursor-pointer hover:border-purple-500/50" onClick={() => fetchLessonDetail(rec.lesson_id)}>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">{rec.relevance_score}% match</Badge>
                              <Badge className={getSeverityColor(rec.lesson_severity || 'medium')}>{rec.lesson_category?.replace('_', ' ')}</Badge>
                            </div>
                            <h5 className="font-medium text-slate-200">{rec.lesson_title}</h5>
                            <p className="text-sm text-slate-400 mt-1">{rec.lesson_summary}</p>
                            <p className="text-xs text-purple-400 mt-2">{rec.reason}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-500" />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAIRecommendDialog(false); setAIRecommendations(null); setAIRecommendContent(''); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate from Investigation Dialog */}
      <Dialog open={showAIGenerateDialog} onOpenChange={setShowAIGenerateDialog}>
        <DialogContent className="max-w-3xl bg-slate-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-purple-400" />
              AI-Generate Lesson from Investigation
            </DialogTitle>
          </DialogHeader>
          
          {!generatedLesson ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-400">Select a completed investigation to automatically generate a lesson learned using AI. The AI will extract key information including what happened, root causes, and recommendations.</p>
              <div className="space-y-2">
                <Label className="text-slate-300">Select Investigation</Label>
                <Select value={selectedInvestigation} onValueChange={setSelectedInvestigation}>
                  <SelectTrigger className="bg-slate-800/50">
                    <SelectValue placeholder="Choose a completed investigation..." />
                  </SelectTrigger>
                  <SelectContent>
                    {investigations.length === 0 ? (
                      <SelectItem value="none" disabled>No completed investigations found</SelectItem>
                    ) : (
                      investigations.map(inv => (
                        <SelectItem key={inv.id} value={inv.id}>
                          {inv.case_number || inv.id} - {inv.title}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={generateLessonFromInvestigation} disabled={aiGenerating || !selectedInvestigation} className="w-full">
                {aiGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                {aiGenerating ? 'Generating Lesson...' : 'Generate Lesson with AI'}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span className="text-sm text-green-300">Lesson generated successfully! Review and edit below before saving.</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Title</Label>
                  <Input 
                    value={generatedLesson.lesson_data?.title || ''}
                    onChange={(e) => updateGeneratedLesson('title', e.target.value)}
                    className="bg-slate-800/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Category</Label>
                  <Select value={generatedLesson.lesson_data?.category || 'flight_operations'} onValueChange={(v) => updateGeneratedLesson('category', v)}>
                    <SelectTrigger className="bg-slate-800/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Summary</Label>
                <Textarea 
                  value={generatedLesson.lesson_data?.summary || ''}
                  onChange={(e) => updateGeneratedLesson('summary', e.target.value)}
                  className="bg-slate-800/50"
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">What Happened?</Label>
                <Textarea 
                  value={generatedLesson.lesson_data?.what_happened || ''}
                  onChange={(e) => updateGeneratedLesson('what_happened', e.target.value)}
                  className="bg-slate-800/50"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Why It Happened (Root Cause)?</Label>
                <Textarea 
                  value={generatedLesson.lesson_data?.why_it_happened || ''}
                  onChange={(e) => updateGeneratedLesson('why_it_happened', e.target.value)}
                  className="bg-slate-800/50"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">What We Learned</Label>
                <Textarea 
                  value={generatedLesson.lesson_data?.what_we_learned || ''}
                  onChange={(e) => updateGeneratedLesson('what_we_learned', e.target.value)}
                  className="bg-slate-800/50"
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">Recommendations ({generatedLesson.lesson_data?.recommendations?.length || 0})</Label>
                <div className="p-3 rounded-lg bg-slate-800/50 space-y-2 max-h-40 overflow-y-auto">
                  {generatedLesson.lesson_data?.recommendations?.map((rec, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                      <span className="text-green-400">•</span>
                      {rec}
                    </div>
                  ))}
                </div>
              </div>

              {generatedLesson.lesson_data?.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-slate-400">Tags:</span>
                  {generatedLesson.lesson_data.tags.map(tag => (
                    <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAIGenerateDialog(false); setGeneratedLesson(null); setSelectedInvestigation(''); }}>
              Cancel
            </Button>
            {generatedLesson && (
              <Button onClick={saveGeneratedLesson} disabled={aiGenerating}>
                {aiGenerating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Save as Draft
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
